import { type FC, useSyncExternalStore } from 'react';
// eslint-disable-next-line import/no-duplicates
import React from 'react';
import { useInView } from 'react-intersection-observer';
import { defineMessages, useIntl } from 'react-intl-next';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Flex, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import EmojiAddIcon from '@atlaskit/icon/core/emoji-add';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import { TransparentErrorBoundary } from '@confluence/error-boundary/entry-points/TransparentErrorBoundary';
import { useContentState } from '@confluence/content-state/entry-points/useContentState';
import { ReactionsSummaryViewPlaceholder } from '../entry-points/ReactionsSummaryViewPlaceholder';
import { ReactionsSSRLoader } from '../entry-points/ReactionsSSRLoader';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { fg } from '@confluence/feature-gating';
import { expVal } from '@confluence/feature-experiments';

import { ReactionsLoaderBackground as DelayedReactions } from './ReactionsLoader';
import type { ReactionLocation } from './types';
import { ReactionContainerType } from './types';
import { useReactionsQueryParams } from './hooks/useReactionsQueryParams';

const i18n = defineMessages({
	addNew: {
		id: 'reactions.view-page.byline.reactions.add.new',
		description: 'Message displayed in button to add a new reaction.',
		defaultMessage: 'Add new',
	},
	addReaction: {
		id: 'reactions.view-page.byline.reactions.add.reaction',
		description: 'Message displayed to add a reaction.',
		defaultMessage: 'Add reaction',
	},
});

const ViewPageReactionsContainerCommonStyles = xcss({
	display: 'flex',
	clear: 'both',
	overflow: 'visible',
	maxWidth: 'unset',
	margin: 'unset',
	marginBottom: 'space.025',
	marginTop: 'space.025',
	height: '28px',
});

type ViewPageReactionsProps = {
	contentId: string;
	contentType?: string | null;
	spaceId?: string | null;
	contentSubType?: string | null;
	noFlexWrap?: boolean;
	noRelativeContainer?: boolean;
};

const TRACKED_NETWORK_EVENTS: (keyof WindowEventMap)[] = ['load', 'online', 'offline'];

const isOnlineSubscribe: Parameters<typeof useSyncExternalStore<boolean>>[0] = (callback) => {
	TRACKED_NETWORK_EVENTS.forEach((event) => {
		window.addEventListener(event, callback);
	});
	return () => {
		TRACKED_NETWORK_EVENTS.forEach((event) => {
			window.removeEventListener(event, callback);
		});
	};
};

const isOnlineGetSnapshot: Parameters<typeof useSyncExternalStore<boolean>>[1] = () =>
	navigator.onLine;

// During SSR rendering, we can't check the navigator status, so instead we'll assume the user is online
const isOnlineGetServerSnapshot: Parameters<typeof useSyncExternalStore<boolean>>[2] = () => true;

const ReactionsComponent: FC<ViewPageReactionsProps> = ({
	contentId,
	contentType,
	spaceId,
	contentSubType,
	noFlexWrap,
	noRelativeContainer,
}) => {
	const { formatMessage } = useIntl();
	const [state] = useContentState();
	const { focusedPageReactionsId } = useReactionsQueryParams();
	const { edition } = useSessionData();
	const [reactionsRef, reactionsSectionInView] = useInView({
		rootMargin: '-100px 0px 0px 0px',
		triggerOnce: true,
	});

	// Used when we want to allow the user to select reactions from the summary view popup
	const customIcon = (
		<EmojiAddIcon
			testId="emoji-add-icon"
			color={token('color.icon.subtle')}
			label={formatMessage(i18n.addReaction)}
		/>
	);

	// Check whether we're on the server, or if in SPA but the page was initially SSR'd
	const isSSR =
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		Boolean(process.env.REACT_SSR || window.__SSR_RENDERED__) &&
		fg('confluence_frontend_content_wrapper');

	// Check whether the reactions section is in view. This is the legacy condition
	const isInViewAndNotOnServer =
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		!process.env.REACT_SSR && reactionsSectionInView && state?.isContentReady;

	const displayReactions = () => {
		if (contentType && spaceId) {
			// We want to load in the background ONLY if we have a query param, otherwise we can preload the query
			const ReactionsLoader = Boolean(focusedPageReactionsId)
				? DelayedReactions
				: ReactionsSSRLoader;

			return (
				<Flex
					testId="view-page-byline-reactions-wrapper"
					data-vc="view-page-byline-reactions-wrapper"
				>
					<ReactionsLoader
						contentId={contentId}
						location={contentType as ReactionLocation}
						containerId={spaceId}
						containerType={ReactionContainerType.SPACE}
						contentSubType={contentSubType}
						summaryViewEnabled
						summaryViewThreshold={1}
						allowSelectFromSummaryView
						useButtonAlignmentStyling
						summaryViewPlacement="bottom-start"
						animateNoReactionsText
						customAnalyticsAttributes={{
							edition,
						}}
						isSubtle
						reactionPickerTriggerIcon={customIcon}
						useDefaultStyledContainer
						noFlexWrap={noFlexWrap}
						noRelativeContainer={noRelativeContainer}
						reactionPickerTriggerText={formatMessage(i18n.addNew)}
						hoverableSummaryView
						optimisticallyLoadSummaryEmojis={isSSR}
						renderParticleEffectOnSummaryView
					/>
				</Flex>
			);
		} else {
			return <ReactionsSummaryViewPlaceholder />;
		}
	};

	return (
		<Box
			ref={reactionsRef}
			testId="view-page-byline-reactions"
			xcss={[ViewPageReactionsContainerCommonStyles]}
		>
			{isSSR || isInViewAndNotOnServer ? displayReactions() : <ReactionsSummaryViewPlaceholder />}
		</Box>
	);
};

const ReactionsComponentWithOfflineReactionsDisabled: FC<ViewPageReactionsProps> = ({
	contentId,
	contentType,
	spaceId,
	contentSubType,
	noFlexWrap,
	noRelativeContainer,
}) => {
	const { formatMessage } = useIntl();
	const [state] = useContentState();
	const { focusedPageReactionsId } = useReactionsQueryParams();
	const { edition } = useSessionData();
	const [reactionsRef, reactionsSectionInView] = useInView({
		rootMargin: '-100px 0px 0px 0px',
		triggerOnce: true,
	});

	// Used when we want to allow the user to select reactions from the summary view popup
	const customIcon = (
		<EmojiAddIcon
			testId="emoji-add-icon"
			color={token('color.icon.subtle')}
			label={formatMessage(i18n.addReaction)}
		/>
	);

	const isOnline = useSyncExternalStore<boolean>(
		isOnlineSubscribe,
		isOnlineGetSnapshot,
		isOnlineGetServerSnapshot,
	);

	// Check whether we're on the server, or if in SPA but the page was initially SSR'd
	const isSSR =
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		Boolean(process.env.REACT_SSR || window.__SSR_RENDERED__) &&
		fg('confluence_frontend_content_wrapper');
	// Check whether the reactions section is in view. This is the legacy condition
	const isInViewAndNotOnServer =
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		!process.env.REACT_SSR && reactionsSectionInView && state?.isContentReady;

	const displayReactions = () => {
		if (contentType && spaceId) {
			// We want to load in the background ONLY if we have a query param, otherwise we can preload the query
			const ReactionsLoader = Boolean(focusedPageReactionsId)
				? DelayedReactions
				: ReactionsSSRLoader;

			return (
				<Flex
					testId="view-page-byline-reactions-wrapper"
					data-vc="view-page-byline-reactions-wrapper"
				>
					<ReactionsLoader
						contentId={contentId}
						location={contentType as ReactionLocation}
						containerId={spaceId}
						containerType={ReactionContainerType.SPACE}
						contentSubType={contentSubType}
						summaryViewEnabled
						summaryViewThreshold={1}
						allowSelectFromSummaryView
						useButtonAlignmentStyling
						summaryViewPlacement="bottom-start"
						animateNoReactionsText
						customAnalyticsAttributes={{
							edition,
						}}
						isSubtle
						reactionPickerTriggerIcon={customIcon}
						useDefaultStyledContainer
						noFlexWrap={noFlexWrap}
						noRelativeContainer={noRelativeContainer}
						reactionPickerTriggerText={formatMessage(i18n.addNew)}
						hoverableSummaryView
						isViewOnly={!isOnline}
						readOnly={!isOnline}
						optimisticallyLoadSummaryEmojis={isSSR}
						renderParticleEffectOnSummaryView
					/>
				</Flex>
			);
		} else {
			return <ReactionsSummaryViewPlaceholder />;
		}
	};

	return (
		<Box
			ref={reactionsRef}
			testId="view-page-byline-reactions"
			xcss={[ViewPageReactionsContainerCommonStyles]}
		>
			{isSSR || isInViewAndNotOnServer ? displayReactions() : <ReactionsSummaryViewPlaceholder />}
		</Box>
	);
};

export const ViewPageBylineReactions: FC<ViewPageReactionsProps> = (props) => {
	return (
		<TransparentErrorBoundary attribution={Attribution.NOTIFICATIONS}>
			{/* Splitting on the experiment here as we're adding a new hook to the component */}
			{expVal('platform_editor_offline_editing_web', 'isEnabled', false) ? (
				<ReactionsComponentWithOfflineReactionsDisabled {...props} />
			) : (
				<ReactionsComponent {...props} />
			)}
		</TransparentErrorBoundary>
	);
};
